import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  LuCoins,
  LuRefreshCw,
  LuDownload,
  LuSettings,
  LuPencil,
  LuCheck,
  LuX,
  LuLock,
  LuShieldCheck,
  LuHourglass,
  LuFlaskConical,
  LuTruck,
  LuScale,
  LuPackage,
  LuCalendarClock,
} from 'react-icons/lu'

import { analyticsService } from '../../../api/services/analytics.service'
import type {
  CashProjectionResponse,
  CashProjectionLot,
  CashProjectionPrice,
  CashUnvaluedReason,
} from '../../../api/models/response/cashProjection'
import { useHasPermission } from '../../../auth/PermissionContext'
import {
  ReportPage,
  ReportHeader,
  ActionButton,
  VerdictBanner,
  KpiGrid,
  KpiTile,
  Panel,
  ToneBadge,
  Notice,
  EmptyState,
  ReportSkeleton,
} from '../../../components/analytics/ReportKit'
import {
  reportAccent,
  TONES,
  type Tone,
  SERIES,
  chart,
  selectStyle,
  table,
} from '../../../components/analytics/reportTheme'
import { fonts, mineral } from '../../../theme'

/* ── Formatos (es-PE, montos en USD) ── */

const usdFmt = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const usdCompactFmt = new Intl.NumberFormat('es-PE', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
})
const usd = (n: number) => usdFmt.format(n)
const usdCompact = (n: number) => usdCompactFmt.format(n)
const num = (n: number, d = 2) => n.toLocaleString('es-PE', { maximumFractionDigits: d })

function parseIsoDate(iso: string): Date {
  return new Date(iso.slice(0, 10) + 'T00:00:00')
}
const fmtDay = (iso: string) =>
  parseIsoDate(iso).toLocaleDateString('es-PE', { weekday: 'short', day: '2-digit', month: 'short' })
const fmtDate = (iso: string) =>
  parseIsoDate(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })

/* ── Catálogos de etiquetas ── */

const PHASE: Record<string, { label: string; icon: typeof LuTruck }> = {
  EnVia: { label: 'En vía / garita', icon: LuTruck },
  BalanzaMuestreo: { label: 'Balanza / muestreo', icon: LuScale },
  Laboratorio: { label: 'Ley de laboratorio', icon: LuFlaskConical },
}

const GRADE_SOURCE: Record<string, { label: string; tone: Tone }> = {
  Laboratorio: { label: 'Ley real', tone: 'ok' },
  HistorialProveedor: { label: 'Historial proveedor', tone: 'info' },
  PromedioPlanta: { label: 'Promedio planta', tone: 'neutral' },
  SinLey: { label: 'Sin ley', tone: 'bad' },
}

const REASON: Record<CashUnvaluedReason, { label: string; hint: (months: number) => string }> = {
  SinPeso: {
    label: 'Sin peso',
    hint: () => 'ni la guía ni la balanza traen un peso legible',
  },
  SinLey: {
    label: 'Sin ley',
    hint: (m) => `sin ensayo propio ni historial de laboratorio en los últimos ${m} meses`,
  },
  SinPrecio: {
    label: 'Sin precio',
    hint: () => 'el metal ensayado no tiene Fixing cargado (sólo hay oro y plata)',
  },
}

const METAL_NAME: Record<string, string> = { AU: 'Oro', AG: 'Plata' }

// Series del horizonte: comprometida (turquesa) + estimada en planta (oro) + estimada en vía (lapislázuli).
const BAR = {
  committed: SERIES[1] ?? mineral.turquesa,
  inPlant: SERIES[2] ?? mineral.oro,
  inTransit: SERIES[3] ?? mineral.lapis,
}

type LotFilter = 'todos' | 'valorizados' | 'sinValor'

/* ══════════════════════════════════════════════════════════════════════════ */

export default function ProyeccionCajaPage() {
  const [data, setData] = useState<CashProjectionResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [snapshotState, setSnapshotState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const canConfigure = useHasPermission('analytics.cashFlow.configure')
  const navigate = useNavigate()

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await analyticsService.cashProjection())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  // Al abrir sólo se LEE. Antes la página disparaba un POST de snapshot al cargar
  // (por eso aparecía "Guardando…" en la barra superior): una pantalla de consulta
  // no escribe sola. La foto cifrada del día ahora es una acción explícita.
  useEffect(() => {
    void fetchData()
  }, [fetchData])

  const saveSnapshot = async () => {
    setSnapshotState('saving')
    try {
      await analyticsService.saveCashSnapshot()
      setSnapshotState('saved')
    } catch {
      setSnapshotState('error')
    }
  }

  const exportCsv = () => {
    if (data) downloadCsv(data)
  }

  return (
    <ReportPage>
      <ReportHeader
        icon={LuCoins}
        title="Proyección de Caja"
        subtitle={
          data
            ? `Efectivo que pedirá el pago a proveedores en los próximos 7 días · corte al ${fmtDate(data.asOf)}`
            : 'Efectivo que pedirá el pago a proveedores en los próximos 7 días'
        }
        actions={
          <>
            {canConfigure && data && (
              <ActionButton
                icon={LuShieldCheck}
                label={snapshotState === 'saved' ? 'Foto guardada' : 'Guardar foto del día'}
                variant="ghost"
                loading={snapshotState === 'saving'}
                disabled={snapshotState === 'saved'}
                title="Guarda una copia cifrada (AES-256) de esta proyección con fecha de hoy. Si ya existe, la reemplaza."
                onClick={saveSnapshot}
              />
            )}
            {canConfigure && data && (
              <ActionButton icon={LuDownload} label="Exportar" onClick={exportCsv} />
            )}
            <ActionButton icon={LuRefreshCw} label="Actualizar" variant="primary" loading={loading} onClick={fetchData} />
          </>
        }
      />

      {error && <Notice tone="bad">No se pudo cargar la proyección: {error}</Notice>}
      {snapshotState === 'error' && (
        <Notice tone="bad">No se pudo guardar la foto del día. Revisa que la clave de cifrado esté configurada.</Notice>
      )}

      {loading && !data && <ReportSkeleton />}

      {data && (
        <motion.div
          key={data.asOf}
          initial={{ opacity: 0 }}
          animate={{ opacity: loading ? 0.55 : 1 }}
          transition={{ duration: 0.25 }}
          style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
        >
          <FixingStrip data={data} canConfigure={canConfigure} onChanged={fetchData} />
          <Verdict data={data} />
          <Kpis data={data} />

          <Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) minmax(0, 2fr)' }} gap={5}>
            <CommittedPanel data={data} />
            <HorizonPanel data={data} />
          </Grid>

          <LotsPanel data={data} />

          <ParamsPanel
            data={data}
            canConfigure={canConfigure}
            onConfigure={() => navigate('/app/analitica/configuracion')}
          />
        </motion.div>
      )}
    </ReportPage>
  )
}

/* ── Franja del Fixing: precio por metal, fecha y edición ── */

function FixingStrip({
  data,
  canConfigure,
  onChanged,
}: {
  data: CashProjectionResponse
  canConfigure: boolean
  onChanged: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [metal, setMetal] = useState('AU')
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const save = async () => {
    const parsed = parseFloat(value.replace(',', '.'))
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setSaveError('Ingresa un precio mayor que cero')
      return
    }
    setBusy(true)
    setSaveError(null)
    try {
      await analyticsService.setCashPrice({ metalCode: metal, pricePerTroyOzUsd: parsed })
      setEditing(false)
      setValue('')
      onChanged()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const startEdit = () => {
    const current = data.prices.find((p) => p.metalCode === metal)
    setValue(current ? String(current.pricePerTroyOzUsd) : '')
    setSaveError(null)
    setEditing(true)
  }

  return (
    <Box
      bg={mineral.surface}
      border={`1px solid ${data.priceStale ? `${TONES.warn.color}66` : mineral.line}`}
      borderRadius="14px"
      px={4}
      py={3}
    >
      <Flex align="center" gap={{ base: 3, md: 5 }} wrap="wrap">
        <Text fontSize="11px" fontWeight={600} color={mineral.ink3} textTransform="uppercase" letterSpacing="0.08em">
          Fixing
        </Text>

        {data.prices.length === 0 && (
          <Text fontSize="13px" color={mineral.ink3}>
            Sin precio cargado — ingresa uno manualmente para valorizar
          </Text>
        )}
        {data.prices.map((p) => (
          <PriceChip key={p.metalCode} price={p} stale={p.priceDate.slice(0, 10) < data.asOf.slice(0, 10)} />
        ))}

        {data.priceStale && data.prices.length > 0 && (
          <ToneBadge tone="warn">Sin fixing de hoy · se usa el más reciente</ToneBadge>
        )}

        {canConfigure && (
          <Flex gap={2} ml="auto" align="center" wrap="wrap">
            <AnimatePresence mode="wait" initial={false}>
              {editing ? (
                <motion.div
                  key="edit"
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.18 }}
                  style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <select
                    aria-label="Metal"
                    value={metal}
                    onChange={(e) => setMetal(e.target.value)}
                    style={{ ...selectStyle, width: 110, height: 38 }}
                  >
                    <option value="AU">Oro (AU)</option>
                    <option value="AG">Plata (AG)</option>
                  </select>
                  <input
                    aria-label="Precio USD por onza troy"
                    inputMode="decimal"
                    placeholder="USD / oz"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void save()
                      if (e.key === 'Escape') setEditing(false)
                    }}
                    style={{ ...selectStyle, width: 120, height: 38 }}
                  />
                  <ActionButton icon={LuCheck} label="Guardar" loading={busy} onClick={save} />
                  <ActionButton icon={LuX} label="Cancelar" variant="ghost" onClick={() => setEditing(false)} />
                </motion.div>
              ) : (
                <motion.div
                  key="view"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  style={{ display: 'flex', gap: 8 }}
                >
                  <ActionButton icon={LuPencil} label="Editar precio" variant="ghost" onClick={startEdit} />
                  <ActionButton
                    icon={LuRefreshCw}
                    label="Refrescar Fixing"
                    variant="ghost"
                    disabled
                    title="El precio se sincroniza automáticamente cada día (06:00, Lima). El refresco manual está deshabilitado para no agotar la cuota mensual de la API (metals.dev, 100 consultas/mes)."
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </Flex>
        )}
      </Flex>
      {saveError && (
        <Text fontSize="12px" color={TONES.bad.deep} mt={2}>
          {saveError}
        </Text>
      )}
    </Box>
  )
}

function PriceChip({ price, stale }: { price: CashProjectionPrice; stale: boolean }) {
  return (
    <Flex align="baseline" gap={2}>
      <Text fontSize="12px" fontWeight={700} color={mineral.ink2}>
        {METAL_NAME[price.metalCode] ?? price.metalCode}
      </Text>
      <Text fontFamily={fonts.display} fontWeight={800} fontSize="17px" color={mineral.ink} letterSpacing="-0.02em">
        {usdFmt.format(price.pricePerTroyOzUsd)}
        <Text as="span" fontSize="12px" fontWeight={500} color={mineral.ink3}>
          {' '}
          /oz
        </Text>
      </Text>
      <Text fontSize="11.5px" color={stale ? TONES.warn.deep : mineral.ink3}>
        {fmtDate(price.priceDate)} · {price.source}
      </Text>
    </Flex>
  )
}

/* ── Veredicto ── */

function Verdict({ data }: { data: CashProjectionResponse }) {
  const unvalued = data.unvaluedLotCount ?? data.lots.filter((l) => !l.valued).length
  const total = data.totalCommittedUsd + data.totalProjectedUsd
  const months = data.config.estimatedGradeHistoryMonths

  if (data.lotCount === 0) {
    return (
      <VerdictBanner
        tone="neutral"
        title="No hay lotes en la cadena"
        subtitle="Sin lotes en vía ni en proceso de planta: no hay pagos a proveedores por aprovisionar."
      />
    )
  }

  const reasons = (data.unvalued ?? [])
    .map((u) => `${u.lotCount} ${REASON[u.reason]?.label.toLowerCase() ?? u.reason}`)
    .join(' · ')

  if (unvalued === data.lotCount) {
    return (
      <VerdictBanner
        tone="bad"
        title={`Ninguno de los ${data.lotCount} lotes pudo valorizarse`}
        subtitle={`La caja no es cero: es desconocida. ${reasons}. Sin ley la proyección necesita ensayos o historial de laboratorio de los últimos ${months} meses.`}
      />
    )
  }

  const meta = (
    <Box>
      <Text fontFamily={fonts.display} fontWeight={800} fontSize="20px" color={mineral.ink}>
        {usd(total)}
      </Text>
      <Text fontSize="11.5px" color={mineral.ink3}>
        demanda a 7 días
      </Text>
    </Box>
  )

  if (unvalued > 0) {
    return (
      <VerdictBanner
        tone="warn"
        title={`Caja subestimada: ${unvalued} de ${data.lotCount} lotes sin valorizar`}
        subtitle={`${usd(total)} con los lotes valorizables; faltan ${reasons}. Revisa la lista de lotes.`}
        meta={meta}
      />
    )
  }

  return (
    <VerdictBanner
      tone="ok"
      title={`Aprovisiona ${usd(total)} para los próximos 7 días`}
      subtitle={`${usd(data.totalCommittedUsd)} ya comprometidos con ley de laboratorio y ${usd(data.totalProjectedUsd)} proyectados con ley estimada.`}
      meta={meta}
    />
  )
}

/* ── Cifras clave ── */

function Kpis({ data }: { data: CashProjectionResponse }) {
  const valuedCommitted = data.lots.filter((l) => l.isCommitted && l.valued).length
  const valuedEstimated = data.lots.filter((l) => !l.isCommitted && l.valued).length
  const valued = data.valuedLotCount ?? data.lots.filter((l) => l.valued).length
  const plantAvg = data.plantAverageLotCount ?? 0

  return (
    <KpiGrid cols={4}>
      <KpiTile
        index={0}
        icon={LuLock}
        tone="ok"
        label="Caja comprometida"
        value={valuedCommitted > 0 ? data.totalCommittedUsd : null}
        format={usd}
        hint={
          valuedCommitted > 0
            ? `${valuedCommitted} lote${valuedCommitted === 1 ? '' : 's'} con ley real de laboratorio`
            : 'Ningún lote con ley de laboratorio aún'
        }
      />
      <KpiTile
        index={1}
        icon={LuHourglass}
        tone="warn"
        label="Caja proyectada (7 días)"
        value={valuedEstimated > 0 ? data.totalProjectedUsd : null}
        format={usd}
        hint={
          valuedEstimated > 0
            ? `${valuedEstimated} lote${valuedEstimated === 1 ? '' : 's'} con ley estimada · castigo ${pct(data.config.cashPenaltyFactorBase)}`
            : 'Sin lotes valorizables con ley estimada'
        }
      />
      <KpiTile
        index={2}
        icon={LuCalendarClock}
        tone="accent"
        label="Demanda total a 7 días"
        value={valued > 0 ? data.totalCommittedUsd + data.totalProjectedUsd : null}
        format={usd}
        hint={(data.beyondHorizonUsd ?? 0) > 0 ? `+ ${usd(data.beyondHorizonUsd ?? 0)} fuera del horizonte` : 'Comprometida + proyectada'}
      />
      <KpiTile
        index={3}
        icon={LuPackage}
        tone={valued < data.lotCount ? 'warn' : 'neutral'}
        label="Lotes valorizados"
        value={`${valued} / ${data.lotCount}`}
        hint={
          plantAvg > 0
            ? `${plantAvg} con ley promedio de planta (menor confianza)`
            : valued < data.lotCount
              ? `${data.lotCount - valued} sin valorizar`
              : 'En vía + en proceso de planta'
        }
      />
    </KpiGrid>
  )
}

const pct = (f: number) => `${num(f * 100, 1)} %`

/* ── Bloque A: Caja comprometida por ventana ── */

function CommittedPanel({ data }: { data: CashProjectionResponse }) {
  const c = data.committed
  const has = data.lots.some((l) => l.isCommitted && l.valued)
  const windows: [string, number][] = [
    // Franjas (no acumuladas): cuánto se paga en cada tramo de 24 h.
    ['En las próximas 24 h', c.within24hUsd],
    ['Entre 24 y 48 h', c.within48hUsd],
    ['Entre 48 y 72 h', c.within72hUsd],
  ]
  const max = Math.max(...windows.map(([, v]) => v), 1)

  return (
    <Panel
      index={0}
      title="Caja comprometida"
      subtitle="Ley real de laboratorio, sin castigo: obligación que Tesorería debe aprovisionar ya"
    >
      {!has ? (
        <EmptyState
          icon={LuFlaskConical}
          title="Sin ley de laboratorio"
          hint="Cuando el laboratorio registre el ensayo de un lote en cadena, su pago aparece aquí."
        />
      ) : (
        <Flex direction="column" gap={3}>
          {windows.map(([label, v], i) => (
            <Box key={label}>
              <Flex justify="space-between" align="baseline" mb={1}>
                <Text fontSize="12.5px" color={mineral.ink2}>
                  {label}
                </Text>
                <Text fontFamily={fonts.display} fontWeight={700} fontSize="15px" color={v > 0 ? mineral.ink : mineral.ink4}>
                  {usd(v)}
                </Text>
              </Flex>
              <Box h="6px" borderRadius="full" bg={mineral.sunk} overflow="hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(v / max) * 100}%` }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease: 'easeOut' }}
                  style={{ height: '100%', background: BAR.committed, borderRadius: 999 }}
                />
              </Box>
            </Box>
          ))}
          <Flex justify="space-between" align="baseline" pt={3} mt={1} borderTop={`1px solid ${mineral.line}`}>
            <Text fontSize="12.5px" fontWeight={600} color={mineral.ink2}>
              Total comprometido · {c.lotCount} lote{c.lotCount === 1 ? '' : 's'}
            </Text>
            <Text fontFamily={fonts.display} fontWeight={800} fontSize="18px" color={TONES.ok.deep}>
              {usd(c.totalUsd)}
            </Text>
          </Flex>
        </Flex>
      )}
    </Panel>
  )
}

/* ── Bloque B: horizonte de 7 días (barras apiladas por día) ── */

interface DayRow {
  dia: string
  committed: number
  inPlant: number
  inTransit: number
  lots: number
  total: number
}

function HorizonPanel({ data }: { data: CashProjectionResponse }) {
  const rows: DayRow[] = useMemo(
    () =>
      data.projectedByDay.map((d) => {
        // Respaldo para respuestas sin el desglose planta / vía: todo lo estimado va a "planta".
        const inTransit = d.inTransitUsd ?? 0
        const inPlant = d.inPlantUsd ?? Math.max(d.estimatedUsd - inTransit, 0)
        return {
          dia: fmtDay(d.date),
          committed: d.committedUsd,
          inPlant,
          inTransit,
          lots: d.lotCount,
          total: d.totalUsd,
        }
      }),
    [data],
  )
  const empty = rows.every((r) => r.total <= 0)

  return (
    <Panel
      index={1}
      title="Horizonte de 7 días"
      subtitle="Desembolso esperado por día según la fase de cada lote (garita → balanza → laboratorio)"
    >
      {empty ? (
        <EmptyState
          icon={LuCalendarClock}
          title="Sin montos en el horizonte"
          hint={
            data.lotCount > 0
              ? 'Hay lotes en cadena, pero ninguno pudo valorizarse. La lista de lotes indica qué le falta a cada uno.'
              : 'No hay lotes en vía ni en planta.'
          }
        />
      ) : (
        <>
          <Box h="280px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ left: 16, right: 8, top: 8, bottom: 0 }} barCategoryGap="24%">
                <CartesianGrid {...chart.grid} />
                <XAxis dataKey="dia" {...chart.axis} />
                <YAxis {...chart.axis} width={80} tickFormatter={(v: number) => usdCompact(v)} />
                <Tooltip content={<HorizonTooltip />} cursor={chart.tooltip.cursor} />
                <Bar dataKey="committed" name="Comprometida" stackId="caja" fill={BAR.committed} isAnimationActive={false} />
                <Bar dataKey="inPlant" name="Estimada · en planta" stackId="caja" fill={BAR.inPlant} isAnimationActive={false} />
                <Bar
                  dataKey="inTransit"
                  name="Estimada · en vía"
                  stackId="caja"
                  fill={BAR.inTransit}
                  radius={[6, 6, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Flex gap={4} wrap="wrap" mt={2}>
            <Legend color={BAR.committed} label="Comprometida (ley real)" />
            <Legend color={BAR.inPlant} label="Estimada · en planta" />
            <Legend color={BAR.inTransit} label="Estimada · en vía" />
          </Flex>
        </>
      )}
    </Panel>
  )
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <Flex align="center" gap={1.5}>
      <Box w="10px" h="10px" borderRadius="3px" bg={color} />
      <Text fontSize="12px" color={mineral.ink2}>
        {label}
      </Text>
    </Flex>
  )
}

function HorizonTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: DayRow }> }) {
  const row = payload?.[0]?.payload
  if (!active || !row) return null
  const lines: [string, string, number][] = [
    ['Comprometida', BAR.committed, row.committed],
    ['En planta', BAR.inPlant, row.inPlant],
    ['En vía', BAR.inTransit, row.inTransit],
  ]
  return (
    <Box style={chart.tooltip.contentStyle}>
      <Text style={chart.tooltip.labelStyle}>{row.dia}</Text>
      {lines
        .filter(([, , v]) => v > 0)
        .map(([label, color, v]) => (
          <Flex key={label} align="center" gap={2} fontSize="12px" color={mineral.ink2}>
            <Box w="8px" h="8px" borderRadius="2px" bg={color} />
            <Text flex={1}>{label}</Text>
            <Text fontWeight={600}>{usd(v)}</Text>
          </Flex>
        ))}
      <Flex justify="space-between" mt={1.5} pt={1.5} borderTop={`1px solid ${mineral.line}`} fontSize="12px">
        <Text color={mineral.ink3}>
          {row.lots} lote{row.lots === 1 ? '' : 's'}
        </Text>
        <Text fontWeight={700} color={mineral.ink}>
          {usd(row.total)}
        </Text>
      </Flex>
    </Box>
  )
}

/* ── Lotes que componen la caja ── */

function LotsPanel({ data }: { data: CashProjectionResponse }) {
  const unvaluedCount = data.lots.filter((l) => !l.valued).length
  const [filter, setFilter] = useState<LotFilter>(unvaluedCount === data.lots.length && unvaluedCount > 0 ? 'sinValor' : 'todos')
  const months = data.config.estimatedGradeHistoryMonths

  const lots = useMemo(() => {
    const list = data.lots.filter((l) =>
      filter === 'valorizados' ? l.valued : filter === 'sinValor' ? !l.valued : true,
    )
    // Valorizados primero (mayor monto arriba); luego los que no se pudieron valorizar.
    return [...list].sort((a, b) => Number(b.valued) - Number(a.valued) || b.estimatedValueUsd - a.estimatedValueUsd)
  }, [data.lots, filter])

  const tabs: [LotFilter, string, number][] = [
    ['todos', 'Todos', data.lots.length],
    ['valorizados', 'Valorizados', data.lots.length - unvaluedCount],
    ['sinValor', 'Sin valorizar', unvaluedCount],
  ]

  return (
    <Panel
      index={2}
      padded={false}
      title="Lotes que componen la caja"
      subtitle="Peso seco × ley × Fixing por lote; los que no se pudieron valorizar dicen qué les falta"
      actions={
        <Flex bg={mineral.sunk} p="3px" borderRadius="10px" gap="2px">
          {tabs.map(([key, label, count]) => (
            <Box
              key={key}
              as="button"
              onClick={() => setFilter(key)}
              position="relative"
              px={2.5}
              h="30px"
              fontSize="12px"
              fontWeight={600}
              color={filter === key ? mineral.ink : mineral.ink3}
              borderRadius="8px"
              cursor="pointer"
            >
              {filter === key && (
                <motion.div
                  layoutId="cash-lot-filter"
                  transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  style={{ position: 'absolute', inset: 0, background: '#fff', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}
                />
              )}
              <Box as="span" position="relative" whiteSpace="nowrap">
                {label} · {count}
              </Box>
            </Box>
          ))}
        </Flex>
      }
    >
      {unvaluedCount > 0 && filter !== 'valorizados' && (
        <Box px={5} pt={4}>
          <Notice tone="warn">
            <Text fontWeight={600} color={mineral.ink}>
              {unvaluedCount} lote{unvaluedCount === 1 ? '' : 's'} no suma{unvaluedCount === 1 ? '' : 'n'} a la caja:
            </Text>
            {(data.unvalued ?? []).map((u) => (
              <Text key={u.reason}>
                <b>{u.lotCount}</b> {REASON[u.reason]?.label.toLowerCase() ?? u.reason} — {REASON[u.reason]?.hint(months) ?? ''}
              </Text>
            ))}
          </Notice>
        </Box>
      )}

      {lots.length === 0 ? (
        <EmptyState
          title={data.lots.length === 0 ? 'No hay lotes en la cadena' : 'Ningún lote en este filtro'}
          hint={data.lots.length === 0 ? 'Sin lotes en vía ni en proceso de planta.' : undefined}
        />
      ) : (
        <Box overflowX="auto" mt={unvaluedCount > 0 && filter !== 'valorizados' ? 3 : 0}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr>
                <th style={table.th}>Lote · proveedor</th>
                <th style={table.th}>Fase</th>
                <th style={{ ...table.th, textAlign: 'right' }}>Peso seco</th>
                <th style={table.th}>Ley</th>
                <th style={{ ...table.th, textAlign: 'right' }}>Valor</th>
                <th style={{ ...table.th, textAlign: 'right' }}>Desembolso</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence initial={false}>
                {lots.map((l, i) => (
                  <LotRow key={l.shipmentRecordId} lot={l} index={i} />
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </Box>
      )}
    </Panel>
  )
}

function LotRow({ lot, index }: { lot: CashProjectionLot; index: number }) {
  const phase = PHASE[lot.phase]
  const PhaseIcon = phase?.icon ?? LuPackage
  const source = GRADE_SOURCE[lot.gradeSource ?? (lot.isCommitted ? 'Laboratorio' : 'HistorialProveedor')]
  const reason = lot.unvaluedReason ? REASON[lot.unvaluedReason] : null
  const metals = lot.metals ?? []

  return (
    <motion.tr
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ delay: Math.min(index * 0.02, 0.3), duration: 0.2 }}
      style={{ background: lot.valued ? undefined : mineral.surfaceAlt }}
    >
      <td style={table.td}>
        <Text fontWeight={700} color={mineral.ink} fontSize="13px">
          {lot.batchCode}
        </Text>
        <Text fontSize="11.5px" color={mineral.ink3} lineClamp={1}>
          {lot.supplierName}
        </Text>
      </td>
      <td style={table.td}>
        <Flex align="center" gap={2}>
          <Box color={lot.isCommitted ? TONES.ok.color : mineral.ink3} display="flex">
            <PhaseIcon size={14} />
          </Box>
          <Text fontSize="12.5px" color={mineral.ink2} whiteSpace="nowrap">
            {phase?.label ?? lot.phase}
          </Text>
        </Flex>
      </td>
      <td style={{ ...table.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {lot.netWeightTms > 0 ? (
          <>
            <Text fontSize="13px" color={mineral.ink}>
              {num(lot.netWeightTms, 3)} TMS
            </Text>
            <Text fontSize="11px" color={mineral.ink3}>
              {lot.weightBasis === 'Balanza' ? 'balanza' : 'guía'}
              {(lot.moisturePct ?? 0) > 0 ? ` · H₂O ${num(lot.moisturePct ?? 0, 1)} %` : ''}
            </Text>
          </>
        ) : (
          <Text color={mineral.ink4}>—</Text>
        )}
      </td>
      <td style={table.td}>
        <Flex direction="column" gap={1} align="flex-start">
          {metals.length > 0 ? (
            <Text fontSize="12.5px" color={mineral.ink2} whiteSpace="nowrap">
              {metals.map((m) => `${m.metalCode} ${num(m.gradeOzPerTon, 3)} oz/t`).join(' · ')}
            </Text>
          ) : lot.gradeOzPerTon > 0 ? (
            <Text fontSize="12.5px" color={mineral.ink2}>
              {lot.metalCode} {num(lot.gradeOzPerTon, 3)} oz/t
            </Text>
          ) : null}
          {source && <ToneBadge tone={source.tone}>{source.label}</ToneBadge>}
        </Flex>
      </td>
      <td style={{ ...table.td, textAlign: 'right' }}>
        {lot.valued ? (
          <Text fontFamily={fonts.display} fontWeight={800} fontSize="14px" color={lot.isCommitted ? TONES.ok.deep : TONES.warn.deep}>
            {usd(lot.estimatedValueUsd)}
          </Text>
        ) : (
          <Flex direction="column" align="flex-end" gap={1}>
            <ToneBadge tone="bad">{reason?.label ?? 'Sin valor'}</ToneBadge>
          </Flex>
        )}
        {lot.valued && lot.penaltyFactor > 0 && (
          <Text fontSize="11px" color={mineral.ink3}>
            castigo {pct(lot.penaltyFactor)}
          </Text>
        )}
      </td>
      <td style={{ ...table.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
        <Text fontSize="12.5px" color={mineral.ink2}>
          {fmtDay(lot.estimatedDisbursementDate)}
        </Text>
        <Text fontSize="11px" color={mineral.ink3}>
          en {lot.leadTimeHours} h
        </Text>
      </td>
    </motion.tr>
  )
}

/* ── Parámetros de valorización (sólo lectura; se editan en Configuración de Analítica) ── */

function ParamsPanel({
  data,
  canConfigure,
  onConfigure,
}: {
  data: CashProjectionResponse
  canConfigure: boolean
  onConfigure: () => void
}) {
  const c = data.config
  const items: [string, string][] = [
    ['Factor de castigo', pct(c.cashPenaltyFactorBase)],
    ['Balanza (mín – máx)', `${c.leadTimeBalanzaHoursMin} – ${c.leadTimeBalanzaHoursMax} h`],
    ['Laboratorio', `${c.leadTimeLabHours} h`],
    ['Historial de ley', `${c.estimatedGradeHistoryMonths} meses`],
  ]
  return (
    <Panel
      index={3}
      title="Parámetros de valorización"
      subtitle="Ley estimada = promedio del laboratorio del proveedor (o de la planta si no tiene historial); peso seco = TMH × (1 − humedad)"
      actions={
        canConfigure ? (
          <ActionButton icon={LuSettings} label="Configurar" variant="ghost" onClick={onConfigure} />
        ) : undefined
      }
    >
      <Grid templateColumns={{ base: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }} gap={4}>
        {items.map(([label, value]) => (
          <Box key={label}>
            <Text fontSize="11px" fontWeight={600} color={mineral.ink3} textTransform="uppercase" letterSpacing="0.06em">
              {label}
            </Text>
            <Text fontFamily={fonts.display} fontWeight={700} fontSize="16px" color={reportAccent.deep} mt={0.5}>
              {value}
            </Text>
          </Box>
        ))}
      </Grid>
    </Panel>
  )
}

/* ── Exportación CSV (cliente, misma estructura que GET /export) ── */

function downloadCsv(data: CashProjectionResponse) {
  const header = [
    'Lote',
    'Proveedor',
    'Estado',
    'Fase',
    'PesoTMH',
    'HumedadPct',
    'PesoNetoTMS',
    'BasePeso',
    'LeyOzPorTon',
    'Metal',
    'PrecioUSDxOz',
    'FactorCastigo',
    'ValorEstimadoUSD',
    'Comprometido',
    'FuenteLey',
    'Valorizado',
    'MotivoSinValor',
    'FechaDesembolso',
  ].join(',')
  const rows = data.lots.map((l) =>
    [
      csv(l.batchCode),
      csv(l.supplierName),
      csv(l.state),
      csv(l.phase),
      l.netWeightTmh ?? '',
      l.moisturePct ?? '',
      l.netWeightTms,
      csv(l.weightBasis ?? ''),
      l.gradeOzPerTon,
      csv(l.metalCode),
      l.pricePerTroyOzUsd,
      l.penaltyFactor,
      l.estimatedValueUsd,
      l.isCommitted ? 'Si' : 'No',
      csv(l.gradeSource ?? ''),
      l.valued ? 'Si' : 'No',
      csv(l.unvaluedReason ?? ''),
      l.estimatedDisbursementDate,
    ].join(','),
  )
  const blob = new Blob(['﻿' + [header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `proyeccion-caja-${data.asOf}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function csv(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"'
  }
  return value
}
